// Implements: F-GEN-033
/**
 * The Mermaid source the diagram frame posts to its parent (owner 2026-10-19, answer 6A), so the
 * legend shows only the keys the displayed diagram uses. The app never holds the diagram text
 * otherwise: it only knows the frame's path.
 *
 * The frame runs in an opaque origin and a sibling frame can post to this window too, so nothing is
 * trusted: `event.source` must be this frame's own window, `event.origin` must be `"null"`, the
 * shape must be `{channel: "prototyper:diagram", v: 1, kind: "source", text}` and the text at most
 * `DIAGRAM_SOURCE_MAX` characters; anything else is ignored. The text is only ever read by
 * `legendKeysOf`, never rendered. A new `resetKey` (another frame path) forgets the source of the
 * previous one (checklist 14): until its own frame posts, there is none.
 */
import { useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

export const DIAGRAM_SOURCE_MAX = 200_000;

const CHANNEL = "prototyper:diagram";

function sourceOf(data: unknown): string | null {
  if (typeof data !== "object" || data === null) return null;
  const { channel, v, kind, text } = data as Record<string, unknown>;
  if (channel !== CHANNEL || v !== 1 || kind !== "source") return null;
  if (typeof text !== "string" || text.length > DIAGRAM_SOURCE_MAX) return null;
  return text;
}

export function useDiagramSource(frame: RefObject<HTMLIFrameElement | null>, resetKey: string): string | null {
  const [posted, setPosted] = useState<{ readonly key: string; readonly source: string } | null>(null);

  // A layout effect, so the listener exists before the frame can post its source.
  useLayoutEffect(() => {
    const listener = (event: MessageEvent): void => {
      const own = frame.current?.contentWindow ?? null;
      if (own === null || event.source !== own) return;
      if (event.origin !== "null") return;
      const source = sourceOf(event.data);
      if (source === null) return;
      setPosted({ key: resetKey, source });
    };
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
    };
  }, [frame, resetKey]);

  return posted !== null && posted.key === resetKey ? posted.source : null;
}
